import { DataGrid, GridPagination } from "@mui/x-data-grid";
import React, { useContext, useEffect, useRef, useState } from "react";
// prop-types is a library for typechecking of props
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  CircularProgress,
  Icon,
  LinearProgress,
  Stack,
} from "@mui/material";
import MaterialBox from "../MaterialBox";
import MaterialInput from "../MaterialInput";
import SearchIcon from "@mui/icons-material/Search";
import MaterialTypography from "../MaterialTypography";
import { Info } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import { DesignationContext } from "../../context/designationcontext";
// import CustomPagination from "./components/CustomPagination"
import CustomizedPagination from "./components/CustomPagination";
 
function CustomPagination(props) {
  return <GridPagination ActionsComponent={CustomizedPagination} {...props} />;
}
const dataGridStyles = {
  columnHeader: {
    // backgroundColor: 'primary.main',
    color: "#ffffff",
  },
  columnHeaderTitle: {
    fontSize: "14px",
    fontWeight: "bold",
    textTransform: "none",
  },
  cellOddRow: {
    backgroundColor: "#ffffff",
  },
  cellEvenRow: {
    backgroundColor: "#ffffff",
  },
  cellText: {
    color: "#000000", // Text color
  },
};
 
const MaterialDataGrid = ({
  rows,
  columns,
  checkboxSelection,
  onRowClick,
  loading,
  localeText,
  isSelectable,
  noSearch,
  subTab,
}) => {
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const gridWrapperRef = useRef(null);
 
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
 
  const { filteredResults } = useContext(DesignationContext);
 
  useEffect(() => {
    if (searchText && searchText.length > 1) {
      const timeoutId = setTimeout(() => {
        getSearchDetails(searchText);
      }, 300); // Debounce search
      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]); // Reset to all rows if searchText is cleared
    }
  }, [searchText, rows]);

  const scrolltoId = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView();
  };

  const getSearchDetails = async (searchText) => {
    setSearchResults([]);
    setIsLoading(true);
    scrolltoId("search");

    if (!hasSearched) setHasSearched(true);

    // Filter rows based on searchText
    const results = rows.filter((row) => {
      // Ensure row and its fields are defined before calling .toString()
      return Object.values(row).some((value) =>
        value?.toString().toLowerCase().includes(searchText.toLowerCase())
      );
    });

    setSearchResults(results);
    setIsLoading(false);
  };
 
  const columnsData = columns.map((column) => ({
    field: column.field,
    headerName:
      typeof column.headerName === "string" ? column.headerName : column.field,
    renderHeader:
      column.headerName && typeof column.headerName === "string"
        ? () => (
            <MaterialTypography
              variant="subtitle2"
              color="black"
              fontWeight="medium"
              fontSize="16px"
            >
              {column.headerName}
            </MaterialTypography>
          )
        : undefined,
    flex: column.flex,
    width: column.width,
    sortable: true,
    renderCell: (params) =>
      column.type === "status" ? (
        <Stack
          direction="row"
          alignItems="center"
          textAlign="center"
          gap={1}
          mt={1.5}
        >
          <Chip
            label={params.row.status?.label}
            size="small"
            icon={params.row.status?.icon || <Info />}
            color={params.row.status?.color}
          />
        </Stack>
      ) : column.type === "images" ? (
        <img className="image" src={params.row.image} alt="list image" />
      ) : (
        <MaterialTypography
          variant="caption"
          color="dark"
          sx={({ typography: { size } }) => ({ fontSize: size.sm })}
        >
          {params.row[column.field]}
        </MaterialTypography>
      ),
  }));
 
  return (
    <MaterialBox>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        p={1}
      >
        <Grid item xs={12} sm={9}>
          {subTab && <MaterialBox>{subTab}</MaterialBox>}
        </Grid>
 
        <Grid item xs={12} sm={3}>
          <MaterialBox display="flex" justifyContent="flex-end" width="100%">
            {!noSearch && (
              <MaterialInput
                label="Search"
                placeholder="Search..."
                fullWidth
                variant="outlined"
                size="small"
                value={searchText}
                inputProps={{
                  maxlength: "15",
                }}
                onChange={(e) => setSearchText(e.target.value)}
                icon={
                  isLoading
                    ? {
                        direction: "right",
                        component: (
                          <CircularProgress color="success" size={18} />
                        ),
                      }
                    : {
                        direction: "right",
                        component: (
                          <Icon color="action" fontSize="small">
                            <SearchIcon />
                          </Icon>
                        ),
                      }
                }
              />
            )}
          </MaterialBox>
        </Grid>
      </Grid>
 
      <Box
        ref={gridWrapperRef}
        sx={{
          width: "100%",
          overflow: "hidden",
          padding: 2,
        }}
      >
        <DataGrid
          rows={
            searchResults && searchResults.length > 0
              ? searchResults
              : filteredResults && filteredResults.length > 0
              ? filteredResults
              : rows
          }
          columns={columnsData}
          style={{
            border: 0,
            cursor: onRowClick && "pointer",
          }}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          slots={{
            pagination: CustomPagination, // 👈 override default footer
            loadingOverlay: (props) => (
              <MaterialBox style={{ overflow: "hidden" }}>
                <LinearProgress color="dark" {...props} />
              </MaterialBox>
            ),
          }}
          loading={loading}
          checkboxSelection={checkboxSelection}
          disableRowSelectionOnClick
          // rowsPerPageOptions={[25, 50, 100]}
          // pageSizeOptions={[5, 10, 25, 50, 100]}
          onRowClick={onRowClick}
          isSelectable={isSelectable}
          density="standard"
          rowHeight={35}
          columnHeaderHeight={35}
          sx={{
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
              width: "1px",
              height: "1px",
            },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
              borderRadius: "5px",
            },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
              backgroundColor: "grey.400",
              borderRadius: "10px",
            },
            "& .MuiDataGrid-columnHeader": dataGridStyles.columnHeader,
            "& .MuiDataGrid-columnHeaderTitle":
              dataGridStyles.columnHeaderTitle,
            "& .MuiDataGrid-cell": {
              padding: "1px",
            },
            "& .MuiDataGrid-columnHeader": dataGridStyles.columnHeader,
            "& .MuiDataGrid-columnHeaderTitle":
              dataGridStyles.columnHeaderTitle,
            "& .MuiDataGrid-cell": {
              padding: "1px",
            },
            //if isSelectable is true
            ...(!isSelectable && {
              "&  .MuiDataGrid-cell": {
                //focus color
                "&:focus": {
                  outline: "none",
                },
              },
            }),
          }}
          disableSelectionOnClick
          localeText={localeText}
        />
      </Box>
    </MaterialBox>
  );
};
 
// Typechecking props for the MaterialDataGrid
MaterialDataGrid.propTypes = {
  rows: PropTypes.number,
  columns: PropTypes.number,
  checkboxSelection: PropTypes.bool,
  isSelectable: PropTypes.bool,
  loading: PropTypes.bool,
  onRowClick: PropTypes.func,
  localeText: PropTypes.object,
};
 
export default MaterialDataGrid;